import { ChefHat, Plus, RotateCcw, Smartphone, StickyNote, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";
import { providerApi, type ProviderFoodOrder, type ProviderFoodOrderStatus, type VenueSettingsRecord } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import { OrderComposer } from "./OrderComposer";
import {
  SLOW_ORDER_MINUTES,
  formatEgp,
  formatWaiting,
  isOpenOrder,
  minutesSince,
  nextOrderStep,
  orderStatusLabel,
  orderStatusTone,
  previousOrderStep,
} from "./order-status";
import { useFoodOrderQueue } from "./useFoodOrderQueue";
import { useVenueMenu } from "./useVenueMenu";

const COLUMNS: Array<{ status: ProviderFoodOrderStatus; titleKey: string; emptyKey: string }> = [
  { status: "pending", titleKey: "New", emptyKey: "No new orders." },
  { status: "preparing", titleKey: "Preparing", emptyKey: "Nothing being prepared." },
  { status: "ready", titleKey: "Ready to serve", emptyKey: "Nothing waiting to be served." },
];

const CANCEL_REASONS = ["Out of stock", "Guest changed their mind", "Entered by mistake", "Guest left"];

function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function OrdersSection() {
  const { t, language } = useAppPreferences();
  const { selectedVenue, sessions, navigateToSection } = useProviderWorkspace();
  const venueId = selectedVenue?.id ?? null;
  const queue = useFoodOrderQueue(venueId);
  const menu = useVenueMenu(venueId);
  const now = useNow();
  const [composerOpen, setComposerOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ProviderFoodOrder | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [feedback, setFeedback] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [showFinished, setShowFinished] = useState(false);
  // Counter sales are charged on the spot, so the builder needs the venue's service fee, VAT and methods.
  const [venueSettings, setVenueSettings] = useState<VenueSettingsRecord | null>(null);
  useEffect(() => {
    setVenueSettings(null);
    if (!venueId) return;
    let cancelled = false;
    providerApi.getVenueSettings(venueId).then((next) => { if (!cancelled) setVenueSettings(next); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [venueId]);

  const activeSessions = useMemo(
    () => sessions.filter((session) => session.status === "active" && session.venue_id === venueId),
    [sessions, venueId],
  );

  const byStatus = useMemo(() => {
    const grouped: Record<string, ProviderFoodOrder[]> = { pending: [], preparing: [], ready: [] };
    for (const order of queue.orders) {
      if (isOpenOrder(order)) grouped[order.status]!.push(order);
    }
    // Oldest first: that is the one to work on next.
    for (const list of Object.values(grouped)) list.sort((a, b) => a.created_at.localeCompare(b.created_at));
    return grouped;
  }, [queue.orders]);
  const finished = useMemo(
    () => queue.orders.filter((order) => !isOpenOrder(order)).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [queue.orders],
  );

  const [cancelError, setCancelError] = useState<string | null>(null);

  const move = async (order: ProviderFoodOrder, status: ProviderFoodOrderStatus, reason?: string) => {
    setFeedback(null);
    const result = await queue.moveOrder(order.id, status, reason);
    if (!result.ok) {
      if (status === "cancelled") setCancelError(result.message);
      else setFeedback({ tone: "danger", text: result.message });
    }
    return result.ok;
  };

  if (!selectedVenue) {
    return <div className="ops-empty py-10">{t("Pick a venue to see its orders.")}</div>;
  }

  return (
    <div className="grid gap-4">
      <section className="ops-surface grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="ops-kicker">{selectedVenue.name}</p>
            <h1 className="ops-page-title">{t("Food & drink orders")}</h1>
            <p className="ops-copy mt-1 text-sm">
              {queue.openCount === 0
                ? t("All caught up. New orders from staff and from the app appear here.")
                : t("{count} open orders. Work oldest first.", { count: queue.openCount })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="ops-button"
              data-variant="secondary"
              onClick={() => navigateToSection("menu")}
              type="button"
            >
              {t("Menu & stock")}
            </button>
            <button
              className="ops-button"
              data-variant="primary"
              onClick={() => { setFeedback(null); setComposerOpen(true); }}
              type="button"
            >
              <Plus size={16} aria-hidden="true" /> {t("New order")}
            </button>
          </div>
        </div>

        {feedback ? (
          <div className="ops-banner" data-tone={feedback.tone} role={feedback.tone === "danger" ? "alert" : "status"}>
            <span className="min-w-0 flex-1">{feedback.text}</span>
            <button aria-label={t("Dismiss")} className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setFeedback(null)} type="button">
              <X size={14} />
            </button>
          </div>
        ) : null}
        {queue.error ? (
          <div className="ops-banner" data-tone="danger" role="alert">
            <span className="min-w-0 flex-1">{queue.error}</span>
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => void queue.reload()} type="button">{t("Retry")}</button>
          </div>
        ) : null}

        <div className="orders-board">
          {COLUMNS.map((column) => {
            const list = byStatus[column.status] ?? [];
            return (
              <section className="orders-column" data-status={column.status} key={column.status} aria-labelledby={`orders-col-${column.status}`}>
                <header className="orders-column-header">
                  <h2 id={`orders-col-${column.status}`}>{t(column.titleKey)}</h2>
                  <span className="ops-chip" data-tone={list.length > 0 ? orderStatusTone(column.status) : undefined}>{list.length}</span>
                </header>
                {list.length === 0 ? (
                  <p className="orders-column-empty">{queue.isLoading && queue.orders.length === 0 ? t("Loading…") : t(column.emptyKey)}</p>
                ) : (
                  <ul className="grid gap-2.5">
                    {list.map((order) => {
                      const waiting = minutesSince(order.created_at, now);
                      const slow = order.status !== "ready" && waiting >= SLOW_ORDER_MINUTES;
                      const next = nextOrderStep(order.status, t);
                      const back = previousOrderStep(order.status);
                      const busy = queue.updatingOrderId === order.id;
                      return (
                        <li className="order-ticket" data-slow={slow ? "true" : undefined} key={order.id} data-testid="order-ticket">
                          <div className="order-ticket-head">
                            <div className="min-w-0">
                              <p className="order-ticket-unit">{order.counter ? t("Counter") : order.unit_name ?? t("Table")}</p>
                              <p className="ops-copy truncate text-xs">{order.guest_name}</p>
                            </div>
                            <span className="order-ticket-age" data-slow={slow ? "true" : undefined} title={new Date(order.created_at).toLocaleTimeString()}>
                              {formatWaiting(waiting, t)}
                            </span>
                          </div>
                          <ul className="order-ticket-items">
                            {order.items.map((item) => (
                              <li key={item.id}><span className="order-ticket-qty">{item.quantity}×</span> {item.item_name}</li>
                            ))}
                          </ul>
                          {order.notes ? (
                            <p className="order-ticket-note"><StickyNote aria-hidden="true" size={14} /> {order.notes}</p>
                          ) : null}
                          <div className="order-ticket-foot">
                            <span className="ops-copy text-xs">
                              {formatEgp(order.total_amount, language)}
                              {order.placed_by_customer ? (
                                <span className="order-ticket-source"><Smartphone aria-hidden="true" size={12} /> {t("From the app")}</span>
                              ) : null}
                            </span>
                          </div>
                          <div className="order-ticket-actions">
                            {next ? (
                              <button
                                className="ops-button flex-1"
                                data-variant="primary"
                                disabled={busy}
                                onClick={() => void move(order, next.status)}
                                type="button"
                              >
                                {busy ? t("Saving…") : next.label}
                              </button>
                            ) : null}
                            {back ? (
                              <button
                                aria-label={t("Move back to {status}", { status: orderStatusLabel(back, t) })}
                                className="ops-button"
                                data-variant="secondary"
                                disabled={busy}
                                onClick={() => void move(order, back)}
                                title={t("Move back to {status}", { status: orderStatusLabel(back, t) })}
                                type="button"
                              >
                                <RotateCcw size={15} />
                              </button>
                            ) : null}
                            <button
                              aria-label={t("Cancel order")}
                              className="ops-button"
                              data-variant="secondary"
                              disabled={busy}
                              onClick={() => { setCancelReason(""); setCancelError(null); setCancelTarget(order); }}
                              title={t("Cancel order")}
                              type="button"
                            >
                              <X size={15} />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </section>

      <section className="ops-surface grid gap-3">
        <button
          aria-expanded={showFinished}
          className="flex items-center justify-between gap-3 text-start"
          onClick={() => setShowFinished((value) => !value)}
          type="button"
        >
          <span>
            <span className="ops-kicker block">{t("Earlier today")}</span>
            <span className="ops-copy text-sm">{t("{count} served or cancelled in the last 6 hours", { count: finished.length })}</span>
          </span>
          <span className="ops-button" data-size="sm" data-variant="secondary">{showFinished ? t("Hide") : t("Show")}</span>
        </button>
        {showFinished ? (
          finished.length === 0 ? (
            <p className="ops-copy text-sm">{t("Nothing yet.")}</p>
          ) : (
            <ul className="grid gap-2">
              {finished.map((order) => (
                <li className="ops-record-dense flex flex-wrap items-center justify-between gap-2" key={order.id}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{[order.counter ? t("Counter") : order.unit_name, order.guest_name].filter(Boolean).join(" · ")}</p>
                    <p className="ops-copy truncate text-xs">
                      {order.items.map((item) => `${item.quantity}× ${item.item_name}`).join(", ")}
                      {order.cancel_reason ? ` — ${order.cancel_reason}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="ops-copy text-xs">{new Date(order.updated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                    <span className="ops-chip" data-tone={orderStatusTone(order.status)}>{orderStatusLabel(order.status, t)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </section>

      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent className="order-composer-dialog">
          <DialogTitle className="flex items-center gap-2"><ChefHat size={18} aria-hidden="true" /> {t("New order")}</DialogTitle>
          <DialogDescription>{t("Pick a table or the counter, tap what they want, then send it to the queue.")}</DialogDescription>
          {composerOpen ? (
            <OrderComposer
              counter={venueId ? { venueId, settings: venueSettings } : undefined}
              menu={menu.items}
              menuError={menu.error}
              menuLoading={menu.isLoading}
              onCancel={() => setComposerOpen(false)}
              onOpenMenuSetup={() => { setComposerOpen(false); navigateToSection("menu"); }}
              onPlaced={(order, receiptNumber) => {
                queue.upsert(order);
                void menu.reload();
                setComposerOpen(false);
                setFeedback({
                  tone: "success",
                  text: order.counter
                    ? t("Paid and sent to the queue. Receipt {number}.", { number: receiptNumber ?? "" })
                    : t("Order sent for {unit}.", { unit: order.unit_name ?? "" }),
                });
              }}
              sessions={activeSessions}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={cancelTarget !== null} onOpenChange={(open) => { if (!open) setCancelTarget(null); }}>
        <DialogContent>
          <DialogTitle>{t("Cancel this order?")}</DialogTitle>
          <DialogDescription>
            {cancelTarget
              ? t("{unit}: {items}. It comes off the bill and any stock it used goes back.", {
                unit: cancelTarget.unit_name ?? "",
                items: cancelTarget.items.map((item) => `${item.quantity}× ${item.item_name}`).join(", "),
              })
              : null}
          </DialogDescription>
          <div className="grid gap-2">
            <span className="ops-label">{t("Reason")}</span>
            <div className="flex flex-wrap gap-1.5">
              {CANCEL_REASONS.map((reason) => (
                <button
                  aria-pressed={cancelReason === t(reason)}
                  className="ops-button"
                  data-size="sm"
                  data-variant={cancelReason === t(reason) ? "primary" : "secondary"}
                  key={reason}
                  onClick={() => setCancelReason(t(reason))}
                  type="button"
                >
                  {t(reason)}
                </button>
              ))}
            </div>
            <input
              aria-label={t("Other reason")}
              className="ops-input"
              maxLength={255}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder={t("Or type a reason")}
              value={cancelReason}
            />
          </div>
          {cancelError ? <div className="ops-banner" data-tone="danger" role="alert">{cancelError}</div> : null}
          <div className="ops-dialog-actions flex flex-wrap justify-end gap-2">
            <button className="ops-button" data-variant="secondary" onClick={() => setCancelTarget(null)} type="button">{t("Keep order")}</button>
            <button
              className="ops-button"
              data-variant="danger"
              disabled={!cancelTarget || queue.updatingOrderId === cancelTarget.id}
              onClick={async () => {
                if (!cancelTarget) return;
                const ok = await move(cancelTarget, "cancelled", cancelReason.trim() || undefined);
                if (ok) {
                  setCancelTarget(null);
                  void menu.reload();
                }
              }}
              type="button"
            >
              {t("Cancel order")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
